import { AdapterProtocol } from "../flatbuffers/generated/archive/adapter-protocol";

/**
 * Valid values for a bank's `logical_port`.
 *
 * The schema field is an int16 (`archive/bank.fbs: logical_port: short`), so no
 * protocol can carry a port past 32767 and the FlatBuffer serializer would wrap
 * anything larger silently (`DataView.setInt16`: 40000 becomes -25536).
 *
 * LobbyVision2 is tighter: LiftCloud stamps the bank's `logical_port` into the
 * telegraph's `bank_num`, which is a uint8, so it refuses anything outside
 * 0..255 with a 400. The forms enforce the same bound before the request is
 * sent rather than letting the operator find out from the server.
 */

/** Lowest port any protocol accepts. */
export const BANK_LOGICAL_PORT_MIN = 0;

/** Lowest LobbyVision2 port: `bank_num` is unsigned. */
export const LOBBYVISION2_LOGICAL_PORT_MIN = 0;

/** Highest LobbyVision2 port: `bank_num` is a uint8. */
export const LOBBYVISION2_LOGICAL_PORT_MAX = 255;

/** Wire bounds of the schema's `short`, for every protocol. */
export const BANK_LOGICAL_PORT_INT16_MIN = -32768;
export const BANK_LOGICAL_PORT_INT16_MAX = 32767;

/**
 * The LobbyVision2 adapter protocol as the upper-case string the forms compare
 * against. Taken from the generated enum's own name for the member, never typed
 * out, so a schema rename breaks this line at compile time instead of turning
 * every comparison silently false.
 */
export const LOBBYVISION2_PROTOCOL: string = AdapterProtocol[AdapterProtocol.LOBBYVISION2];

/** True when the adapter protocol (any case) is LobbyVision2. */
export function isLobbyVision2(protocolUpper: string): boolean {
  return protocolUpper.toUpperCase() === LOBBYVISION2_PROTOCOL;
}

/** Lowest logical port the protocol allows, for the input's `min` and the message. */
export function logicalPortMin(protocolUpper: string): number {
  return isLobbyVision2(protocolUpper) ? LOBBYVISION2_LOGICAL_PORT_MIN : BANK_LOGICAL_PORT_MIN;
}

/**
 * Highest logical port the protocol allows, for the input's `max`. An empty
 * protocol (no adapter assigned yet) gets the int16 cap alone.
 */
export function logicalPortMax(protocolUpper: string): number {
  return isLobbyVision2(protocolUpper)
    ? LOBBYVISION2_LOGICAL_PORT_MAX
    : BANK_LOGICAL_PORT_INT16_MAX;
}

/**
 * Whether `port` is a value the protocol will not accept. An unset port
 * (null/undefined) is never out of range: the forms omit it from the request
 * and the server applies its default.
 */
export function logicalPortOutOfRange(
  protocolUpper: string,
  port: number | null | undefined,
): boolean {
  if (port === null || port === undefined) return false;
  return port < logicalPortMin(protocolUpper) || port > logicalPortMax(protocolUpper);
}
